import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import { getContactSearchDetailsActionCreator } from "../DataEntryDetailsDucks";
import MatchGridModal, { MatchGridColumn, MatchGridRow } from "./MatchGridModal";
import CreateEntityFormModal, { CreateEntityField } from "./CreateEntityFormModal";
import { CLASSIC_PHONE_REGEX, INVALID_PHONE_MESSAGE } from "./quickEntryFieldConstraints";

/** A contact row as `generateContactSearchReponseObject` shapes it (appUtils.ts). */
export interface RequestorContact {
    readonly id: number;
    readonly firstName: string;
    readonly lastName: string;
    readonly phone: string;
    readonly fax: string;
    readonly email: string;
}

/** The five fields the add-new-contact form captures - the block that used to sit on the create-requestor form. */
export interface NewContactValues {
    readonly contactFirstName: string;
    readonly contactLastName: string;
    readonly email: string;
    readonly phone: string;
    readonly fax: string;
}

export interface AssignContactModalOwnProps {
    /** The resolved requestor whose contacts these are. Undefined => nothing to list, so the popup opens straight on the add-new form. */
    readonly requestorId?: number;
    /** An EXISTING contact was picked from the grid. */
    readonly onSelectContact: (contact: RequestorContact) => void;
    /** The add-new form was saved. The caller persists + links it (this component performs no writes). */
    readonly onCreateContact: (values: NewContactValues) => void;
    readonly onClose: () => void;
    /** AI-extracted contact values, used to pre-fill the add-new form so the document data isn't retyped. */
    readonly extractedContactFirstName?: string;
    readonly extractedContactLastName?: string;
    readonly extractedEmail?: string;
    readonly extractedFax?: string;
    readonly extractedPhone?: string;
}

interface AssignContactModalStateProps {
    readonly contactSearchDetails?: readonly RequestorContact[];
}

interface AssignContactModalDispatchProps {
    readonly getContactSearchDetails: (requestorId: number, distinct?: boolean) => void;
}

type Props = AssignContactModalOwnProps & AssignContactModalStateProps & AssignContactModalDispatchProps;

const COLUMNS: readonly MatchGridColumn[] = [
    { id: "firstName", label: "First name", sortable: true },
    { id: "lastName", label: "Last name" },
    { id: "phone", label: "Phone" },
    { id: "fax", label: "Fax" },
    { id: "email", label: "Email" },
];

/**
 * Assign Contact (Quick Entry). Opened from the resolved requestor row: lists the
 * contacts ALREADY on that requestor so one can be picked, with "+ Add new Contact"
 * for when none fits - the same two-step the classic requestor panel offers, as two
 * popups instead of a grid plus inline fields.
 *
 * Reuses the grid + form primitives this pane already has (MatchGridModal for the
 * filterable list, CreateEntityFormModal for the detail form), and the contacts the
 * store already knows how to fetch (getContactSearchDetails -> contactSearchDetails,
 * shaped {id, firstName, lastName, phone, fax, email} - the exact column set).
 *
 * Writes nothing itself: picking or creating reports UP, and the parent owns
 * persistence + the amber gate. `distinct: true` matches ResendPopup's call, which
 * de-dupes the requestor's contact list.
 *
 * PHI note (hipaa-compliance.md): contact values are requestor PII - never logged.
 */
export const AssignContactModal: React.FC<Props> = (props) => {
    // A requestor with no contacts (e.g. one just created) has nothing to pick from, so
    // open on the form rather than an empty grid the specialist has to click through.
    const [addingNew, setAddingNew] = useState<boolean>(props.requestorId == null);

    useEffect(() => {
        if (props.requestorId != null) {
            props.getContactSearchDetails(props.requestorId, true);
        }
        // Refetch only when the requestor changes - not on every parent render.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.requestorId]);

    const contacts: readonly RequestorContact[] = props.contactSearchDetails ?? [];

    const rows: readonly MatchGridRow[] = contacts.map(contact => ({
        id: contact.id,
        values: {
            firstName: contact.firstName ?? "",
            lastName: contact.lastName ?? "",
            phone: contact.phone ?? "",
            fax: contact.fax ?? "",
            email: contact.email ?? "",
        },
    }));

    const onSelect = (id: string | number) => {
        const picked = contacts.find(contact => contact.id === id);
        if (picked) {
            props.onSelectContact(picked);
        }
    };

    // Same field set (and caps/placeholders) the create-requestor form used to carry,
    // so moving the block here changed where it lives, not what it captures.
    const createFields: readonly CreateEntityField[] = [
        { name: "contactFirstName", label: "Contact First Name", value: props.extractedContactFirstName, maxLength: 30 },
        { name: "contactLastName", label: "Contact Last Name", value: props.extractedContactLastName, maxLength: 30 },
        { name: "email", label: "Email", value: props.extractedEmail, maxLength: 50 },
        // AB#35154: `type: "phone"` masks these as they're typed via the shared
        // normalizePhone. Placeholders and caps are CLASSIC's, unchanged
        // (RequestorContactPanel / Accordion) - the bug was that no mask was applied at
        // all, not that the shape was wrong, and a contact field should not read or
        // behave differently depending on which pane it was opened from.
        //
        // Phone's XXX-XXX-XXXXxXXXXX is the extension form: a lowercase "x" then up to a
        // 5-digit extension, which is exactly what normalizePhone emits past 10 digits.
        // Fax's 12-char cap is a complete XXX-XXX-XXXX to the character, so it holds
        // itself to 10 digits and never reaches the extension form.
        //
        // AB#37970 (David Heifetz, PR !9947 thread 53163): the same Classic-parity validator the
        // Edit Requestor form's Phone now carries. This is the OTHER door onto the same requestor
        // contact phone - the add-new-contact form - so leaving it unvalidated would let the
        // reported `465-768-7697x989689879` in through here instead. Post-hoc, gating Save; the
        // mask is unchanged.
        {
            name: "phone",
            label: "Phone",
            value: props.extractedPhone,
            type: "phone",
            placeholder: "XXX-XXX-XXXXxXXXXX",
            maxLength: 30,
            pattern: CLASSIC_PHONE_REGEX,
            invalidMessage: INVALID_PHONE_MESSAGE,
        },
        { name: "fax", label: "Fax", value: props.extractedFax, type: "phone", placeholder: "XXX-XXX-XXXX", maxLength: 12 },
    ];

    const onCreateSubmit = (values: Readonly<Record<string, string>>) => {
        props.onCreateContact({
            contactFirstName: values.contactFirstName ?? "",
            contactLastName: values.contactLastName ?? "",
            email: values.email ?? "",
            phone: values.phone ?? "",
            fax: values.fax ?? "",
        });
    };

    if (addingNew) {
        return (
            <CreateEntityFormModal
                title="Add New Contact"
                subtitle="Enter the contact's details. The contact is saved against the selected requestor."
                fields={createFields}
                submitLabel="Save Contact"
                onSubmit={onCreateSubmit}
                onClose={props.onClose}
            />
        );
    }

    return (
        <MatchGridModal
            title="Assign Contact"
            subtitle="Select a contact for this requestor, or add a new one."
            columns={COLUMNS}
            rows={rows}
            onSelect={onSelect}
            createNewLabel="+ Add new Contact"
            onCreateNew={() => setAddingNew(true)}
            onClose={props.onClose}
        />
    );
};

function mapStateToProps(state: any): AssignContactModalStateProps {
    return {
        contactSearchDetails: state.dataEntryState?.contactSearchDetails,
    };
}

export function mapDispatchToProps(dispatch: Function): AssignContactModalDispatchProps {
    return {
        getContactSearchDetails: (requestorId: number, distinct?: boolean) =>
            dispatch(getContactSearchDetailsActionCreator(requestorId, distinct)),
    };
}

export default connect(mapStateToProps, mapDispatchToProps)(AssignContactModal);
